import { useState, useCallback, useEffect } from 'react';
import { Popup } from 'devextreme-react/popup';
import TreeList, { Column, SearchPanel, Selection } from 'devextreme-react/tree-list';
import { Button } from 'devextreme-react/button';
import { MOCK_ACCOUNTS } from '../mock-data';
import type { AccountFilter } from '@calculom/report-designer';

interface AccountFilterDialogProps {
  visible: boolean;
  currentFilter: AccountFilter;
  onApply: (filter: AccountFilter) => void;
  onCancel: () => void;
}

export function AccountFilterDialog({
  visible,
  currentFilter,
  onApply,
  onCancel,
}: AccountFilterDialogProps) {
  const [selectedCodes, setSelectedCodes] = useState<string[]>(
    currentFilter.codes ?? [],
  );

  useEffect(() => {
    if (visible) {
      setSelectedCodes(currentFilter.codes ?? []);
    }
  }, [visible, currentFilter.codes]);

  const handleSelectionChanged = useCallback((e: { selectedRowKeys?: string[] }) => {
    setSelectedCodes(e.selectedRowKeys ?? []);
  }, []);

  const handleApply = useCallback(() => {
    onApply({
      ...currentFilter,
      codes: selectedCodes.length > 0 ? selectedCodes : undefined,
    });
  }, [currentFilter, selectedCodes, onApply]);

  return (
    <Popup
      visible={visible}
      onHiding={onCancel}
      title="Account Filter"
      width={600}
      height={500}
      showCloseButton
    >
      {visible && (
        <div className="flex flex-col h-full">
          <div className="flex-1 overflow-hidden">
            <TreeList
              dataSource={MOCK_ACCOUNTS}
              keyExpr="code"
              parentIdExpr="parent"
              rootValue={null as unknown as string}
              showBorders
              showRowLines
              columnAutoWidth
              autoExpandAll
              selectedRowKeys={selectedCodes}
              onSelectionChanged={handleSelectionChanged}
            >
              <SearchPanel visible placeholder="Search accounts..." />
              <Selection mode="multiple" recursive />
              <Column dataField="code" caption="Code" width={80} />
              <Column dataField="name" caption="Name" />
              <Column dataField="type" caption="Type" width={100} />
            </TreeList>
          </div>

          <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-gray-200">
            <Button text="Cancel" stylingMode="outlined" onClick={onCancel} />
            <Button text="Apply" type="default" onClick={handleApply} />
          </div>
        </div>
      )}
    </Popup>
  );
}
